import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, FileX } from "lucide-react";

import EmailTemplateForm from "../../../components/Admin/EmailTemplates/EmailTemplateForm";
import EmailTemplatePreview from "../../../components/Admin/EmailTemplates/EmailTemplatePreview";

import {
  useCreateEmailTemplateMutation,
  useLazyGetEmailTemplateByIdQuery,
  usePreviewEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { ICreateEmailTemplateRequest } from "../../../types/Request/AdminRequest";

import { useToast } from "../../../utils/useToast";
import ToastContainer from "../../../utils/ToastContainer";

const NAVIGATE_TARGET = "/admin/adminEmailTemplates/EmailTemplates";

// Helper function to format words into PascalCase dot-separated keys
const generateTemplateKey = (name: string, category?: string): string => {
  const sanitizeAndFormat = (str: string) =>
    str
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(".");

  const formattedName = sanitizeAndFormat(name || "");
  const formattedCategory = category ? sanitizeAndFormat(category) : "";

  // If the formatted name doesn't start with the category, prefix it
  if (
    formattedCategory &&
    !formattedName.toLowerCase().startsWith(formattedCategory.toLowerCase())
  ) {
    return `${formattedCategory}.${formattedName}`;
  }

  return formattedName;
};

const EmailTemplateDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);

  const [getEmailTemplateById, { data: template, isFetching, isError }] =
    useLazyGetEmailTemplateByIdQuery();

  const [createEmailTemplate, { isLoading: isCreating }] =
    useCreateEmailTemplateMutation();

  const [updateEmailTemplate, { isLoading: isUpdating }] =
    useUpdateEmailTemplateMutation();

  const [previewTemplate, { isLoading: isPreviewing }] =
    usePreviewEmailTemplateMutation();

  const isSubmitting = isCreating || isUpdating;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");

  // --------------------------------------------------
  // Toast
  // --------------------------------------------------

  const { toasts, addToast, removeToast } = useToast();

  // --------------------------------------------------
  // Load template
  // --------------------------------------------------

  useEffect(() => {
    if (isEdit && id) {
      getEmailTemplateById(Number(id));
    }
  }, [id, isEdit, getEmailTemplateById]);

  // --------------------------------------------------
  // Navigation
  // --------------------------------------------------

  const handleBack = () => {
    navigate(NAVIGATE_TARGET);
  };

  // --------------------------------------------------
  // Save Template
  // --------------------------------------------------

  const handleSave = async (formData: ICreateEmailTemplateRequest) => {
    try {
      if (isEdit && template) {
        const { templateKey, ...updateData } = formData;

        console.log("Update Request:", {
          id: template.id,
          data: updateData,
        });

        await updateEmailTemplate({
          id: template.id,
          data: updateData as ICreateEmailTemplateRequest,
        }).unwrap();

        addToast(
          "success",
          "Template Updated",
          "Email template updated successfully.",
        );
      } else {
        // Always generate templateKey automatically when creating
        const templateName =
          (formData as any).name ||
          (formData as any).templateName ||
          "";

        const category = (formData as any).category || "";

        const autoGeneratedKey = generateTemplateKey(templateName, category);

        const payload: ICreateEmailTemplateRequest = {
          ...formData,
          templateKey: autoGeneratedKey,
        };

        console.log("Create Request (Auto-generated Key):", payload);

        await createEmailTemplate(payload).unwrap();

        addToast(
          "success",
          "Template Created",
          "Email template created successfully.",
        );
      }

      // Give the toast a moment to be visible
      setTimeout(() => {
        navigate(NAVIGATE_TARGET);
      }, 800);
    } catch (error: any) {
      console.error("Email template save failed:", error);

      const message =
        error?.data?.Message ||
        error?.data?.message ||
        "Failed to save email template.";

      addToast("error", "Save Failed", message);
    }
  };

  // --------------------------------------------------
  // Preview Template
  // --------------------------------------------------

  const handlePreview = async (
    templateKey: string,
    variables: Record<string, string>,
  ) => {
    try {
      const result = await previewTemplate({
        templateKey,
        variables,
      }).unwrap();

      setPreviewSubject(result.subject ?? "");

      setPreviewHtml(result.htmlBody ?? "");

      setPreviewOpen(true);
    } catch (error: any) {
      console.error("Email template preview failed:", error);

      const message =
        error?.data?.Message ||
        error?.data?.message ||
        "Failed to preview email template.";

      addToast("error", "Preview Failed", message);
    }
  };

  // --------------------------------------------------
  // Initial Loading State
  // --------------------------------------------------

  if (isEdit && isFetching && !template) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center space-y-4 bg-white">
        <div className="relative flex items-center justify-center">
          <div className="h-16 w-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />

          <Loader2 className="absolute text-primary animate-pulse" size={24} />
        </div>

        <p className="text-sm font-medium text-slate-500 animate-pulse">
          Loading email template...
        </p>
      </div>
    );
  }

  // --------------------------------------------------
  // Not Found / Error State
  // --------------------------------------------------

  if (isEdit && (isError || (!isFetching && !template))) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-slate-50 p-8 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-red-500 shadow-sm ring-1 ring-red-100">
          <FileX size={32} />
        </div>

        <h2 className="text-xl font-semibold text-slate-900">
          Template Not Found
        </h2>

        <p className="mt-1.5 max-w-sm text-sm text-slate-500">
          The email template you are trying to edit does not exist or may have
          been removed.
        </p>

        <button
          onClick={handleBack}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-slate-800 active:scale-95"
        >
          <ArrowLeft size={16} />
          Back to Templates
        </button>
      </div>
    );
  }

  // --------------------------------------------------
  // Main Layout
  // --------------------------------------------------

  return (
    <div className="flex min-h-screen w-full flex-col bg-slate-50/50">
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Full-width Header Bar */}
      <div className="w-full border-b border-slate-200 bg-white px-6 py-4 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
              title="Back to Templates"
            >
              <ArrowLeft
                size={18}
                className="transition-transform group-hover:-translate-x-0.5"
              />
            </button>

            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  {isEdit ? "Edit Email Template" : "Create Email Template"}
                </h1>
              </div>

              <p className="mt-1 text-sm text-slate-500">
                {isEdit
                  ? "Modify content, placeholders, and structure for this notification."
                  : "Design and configure a new dynamic email template."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Section */}
      <div className="relative flex-1 w-full p-6 sm:p-8">
        <div className="relative h-full w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-950/5">
          {/* Visual Header Accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-primary via-indigo-500 to-sky-500" />

          {/* Dynamic Loading Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px] transition-all">
              <div className="flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-lg">
                <Loader2 size={18} className="animate-spin text-primary-400" />

                <span className="text-sm font-medium">
                  {isEdit ? "Saving updates..." : "Creating template..."}
                </span>
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="p-6 sm:p-8">
            <EmailTemplateForm
              template={template || null}
              loading={isSubmitting || isFetching}
              onClose={handleBack}
              onSubmit={handleSave}
              onPreview={handlePreview}
              previewLoading={isPreviewing}
            />
          </div>
        </div>
      </div>

      {/* Preview */}
      <EmailTemplatePreview
        open={previewOpen}
        html={previewHtml}
        subject={previewSubject}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default EmailTemplateDetailsScreen;